import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { MessageSquare, Users, Settings, ScrollText, Send, Plus, Trash2, Edit, Zap, CheckCircle, XCircle, Loader2, Save, Phone, Mail, Shield } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_LABELS = {
  tenant_activity: { label: 'Aktivitas Tenant', desc: 'Tenant baru, perubahan status, hapus tenant' },
  saas_billing: { label: 'SaaS Billing', desc: 'Subscription baru, approve, reject, expired' },
  affiliate: { label: 'Affiliate', desc: 'Affiliate baru, penarikan komisi' },
  error_system: { label: 'Error Sistem', desc: 'Error pada webhook, payment, atau sistem lainnya' },
  high_transaction: { label: 'Transaksi Besar', desc: 'Transaksi melebihi threshold yang ditentukan' },
  domain_changes: { label: 'Perubahan Domain', desc: 'Custom domain baru, perubahan DNS' },
  fraud_alerts: { label: 'Alert Fraud', desc: 'Aktivitas mencurigakan terdeteksi' },
};

export default function AdminWANotifications() {
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>WA Notifikasi Admin</h1>
        <p className="text-sm text-slate-500 mt-1">Kelola notifikasi WhatsApp untuk Super Admin via Responic</p>
      </div>
      <Tabs defaultValue="gateway">
        <TabsList className="mb-6 flex-wrap">
          <TabsTrigger value="gateway"><MessageSquare size={15} className="mr-1.5" />WA Gateway</TabsTrigger>
          <TabsTrigger value="recipients"><Users size={15} className="mr-1.5" />Penerima</TabsTrigger>
          <TabsTrigger value="controls"><Settings size={15} className="mr-1.5" />Kontrol</TabsTrigger>
          <TabsTrigger value="logs"><ScrollText size={15} className="mr-1.5" />Log</TabsTrigger>
          <TabsTrigger value="broadcast"><Send size={15} className="mr-1.5" />Broadcast</TabsTrigger>
        </TabsList>
        <TabsContent value="gateway"><GatewayTab /></TabsContent>
        <TabsContent value="recipients"><RecipientsTab /></TabsContent>
        <TabsContent value="controls"><ControlsTab /></TabsContent>
        <TabsContent value="logs"><LogsTab /></TabsContent>
        <TabsContent value="broadcast"><BroadcastTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ============================================================
// TAB 1: WA GATEWAY
// ============================================================
function GatewayTab() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [form, setForm] = useState({ api_key: '', device_id: '', sender_name: '', is_active: false, email_fallback: true });

  useEffect(() => {
    api.get('/admin/wa-gateway').then(res => {
      setConfig(res.data);
      setForm({
        api_key: '', // Don't prefill API key for security
        device_id: res.data.device_id || '',
        sender_name: res.data.sender_name || 'Tokomoe Admin',
        is_active: res.data.is_active || false,
        email_fallback: res.data.email_fallback !== false,
      });
    }).catch(() => toast.error('Gagal memuat konfigurasi')).finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.api_key) delete payload.api_key; // Don't overwrite if empty
      await api.put('/admin/wa-gateway', payload);
      toast.success('Konfigurasi WA Gateway disimpan');
    } catch { toast.error('Gagal menyimpan'); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post('/admin/wa-gateway/test');
      setTestResult(res.data);
      res.data.success ? toast.success(res.data.message) : toast.error(res.data.message);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Gagal mengirim test';
      setTestResult({ success: false, message: msg });
      toast.error(msg);
    } finally { setTesting(false); }
  };

  if (loading) return <Spinner />;

  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader>
        <CardTitle className="text-base">Konfigurasi Responic Gateway</CardTitle>
        <p className="text-sm text-slate-500 mt-1">API token Responic untuk mengirim notifikasi WhatsApp ke admin</p>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-center justify-between p-4 rounded-xl border border-slate-200 bg-slate-50">
          <div>
            <p className="font-medium text-slate-900">Aktifkan WA Gateway</p>
            <p className="text-sm text-slate-500">Kirim notifikasi WhatsApp ke admin saat ada event penting</p>
          </div>
          <Switch checked={form.is_active} onCheckedChange={v => set('is_active', v)} />
        </div>
        {form.is_active && (
          <div className="p-4 rounded-xl border border-emerald-200 bg-emerald-50 flex gap-3">
            <CheckCircle size={18} className="text-emerald-600 shrink-0 mt-0.5" />
            <p className="text-sm text-emerald-700">WA Gateway aktif. Notifikasi admin akan dikirim via WhatsApp.</p>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="space-y-2">
            <Label>API Token Responic</Label>
            <Input type="password" value={form.api_key} onChange={e => set('api_key', e.target.value)} placeholder={config?.api_key_display || 'Masukkan Bearer token Responic'} />
            <p className="text-xs text-slate-500">Dapatkan di Responic dashboard → Account → pilih akun → Api Token.</p>
            {config?.env_api_key_set && !config?.api_key && (
              <p className="text-xs text-blue-600">ℹ️ Menggunakan token dari environment variable</p>
            )}
          </div>
          <div className="space-y-2">
            <Label>Device ID (opsional)</Label>
            <Input value={form.device_id} onChange={e => set('device_id', e.target.value)} placeholder="Tidak diperlukan untuk Responic" disabled />
            <p className="text-xs text-slate-500">Responic tidak memakai device ID — field ini diabaikan.</p>
          </div>
          <div className="space-y-2">
            <Label>Nama Pengirim</Label>
            <Input value={form.sender_name} onChange={e => set('sender_name', e.target.value)} placeholder="Tokomoe Admin" />
          </div>
          <div className="flex items-center gap-3 pt-6">
            <Switch checked={form.email_fallback} onCheckedChange={v => set('email_fallback', v)} />
            <div>
              <p className="text-sm font-medium text-slate-700">Email Fallback</p>
              <p className="text-xs text-slate-500">Kirim email jika WhatsApp gagal terkirim</p>
            </div>
          </div>
        </div>
        {testResult && (
          <div className={`p-4 rounded-xl border flex gap-3 ${testResult.success ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}>
            {testResult.success ? <CheckCircle size={18} className="text-emerald-600 shrink-0 mt-0.5" /> : <XCircle size={18} className="text-red-600 shrink-0 mt-0.5" />}
            <p className={`text-sm ${testResult.success ? 'text-emerald-700' : 'text-red-700'}`}>{testResult.message}</p>
          </div>
        )}
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={test} disabled={testing}>
            {testing ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Zap size={16} className="mr-2" />}
            {testing ? 'Mengirim...' : 'Test Kirim WA'}
          </Button>
          <Button onClick={save} disabled={saving} className="bg-[#2D6CDF] hover:bg-[#2D6CDF]/90">
            <Save size={16} className="mr-2" />{saving ? 'Menyimpan...' : 'Simpan'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================
// TAB 2: RECIPIENTS
// ============================================================
function RecipientsTab() {
  const [recipients, setRecipients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', email: '', role: 'admin', categories: [], is_active: true });

  const fetch = useCallback(async () => {
    try {
      const res = await api.get('/admin/notification-recipients');
      setRecipients(res.data.recipients);
      setCategories(res.data.available_categories);
    } catch { toast.error('Gagal memuat data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const openAdd = () => {
    setEditing(null);
    setForm({ name: '', phone: '', email: '', role: 'admin', categories: Object.keys(CATEGORY_LABELS), is_active: true });
    setDialogOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ name: r.name, phone: r.phone, email: r.email || '', role: r.role || 'admin', categories: r.categories || [], is_active: r.is_active });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.phone) return toast.error('Nama dan nomor HP wajib diisi');
    try {
      if (editing) {
        await api.put(`/admin/notification-recipients/${editing.id}`, form);
        toast.success('Penerima diupdate');
      } else {
        await api.post('/admin/notification-recipients', form);
        toast.success('Penerima ditambahkan');
      }
      setDialogOpen(false);
      fetch();
    } catch { toast.error('Gagal menyimpan'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Hapus penerima ini?')) return;
    try {
      await api.delete(`/admin/notification-recipients/${id}`);
      toast.success('Penerima dihapus');
      fetch();
    } catch { toast.error('Gagal menghapus'); }
  };

  const toggleCat = (cat) => {
    setForm(p => ({
      ...p,
      categories: p.categories.includes(cat) ? p.categories.filter(c => c !== cat) : [...p.categories, cat]
    }));
  };

  if (loading) return <Spinner />;

  return (
    <>
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Penerima Notifikasi</CardTitle>
            <p className="text-sm text-slate-500 mt-1">Daftar admin yang menerima notifikasi WhatsApp</p>
          </div>
          <Button size="sm" onClick={openAdd} className="bg-[#2D6CDF] hover:bg-[#2D6CDF]/90">
            <Plus size={16} className="mr-1.5" />Tambah
          </Button>
        </CardHeader>
        <CardContent>
          {recipients.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Users size={40} className="mx-auto mb-3 opacity-50" />
              <p>Belum ada penerima. Klik "Tambah" untuk menambahkan.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recipients.map(r => (
                <div key={r.id} className="flex items-center justify-between p-4 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold ${r.is_active ? 'bg-[#2D6CDF]' : 'bg-slate-300'}`}>
                      {r.name?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{r.name}</p>
                      <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                        <span className="flex items-center gap-1"><Phone size={12} />{r.phone}</span>
                        {r.email && <span className="flex items-center gap-1"><Mail size={12} />{r.email}</span>}
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {(r.categories || []).map(c => (
                          <Badge key={c} variant="secondary" className="text-[10px] px-1.5 py-0">{CATEGORY_LABELS[c]?.label || c}</Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={r.is_active ? 'default' : 'secondary'} className={r.is_active ? 'bg-emerald-100 text-emerald-700' : ''}>{r.is_active ? 'Aktif' : 'Nonaktif'}</Badge>
                    <Button variant="ghost" size="icon" onClick={() => openEdit(r)}><Edit size={16} /></Button>
                    <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700" onClick={() => remove(r.id)}><Trash2 size={16} /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Penerima' : 'Tambah Penerima'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Nama *</Label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Nama admin" />
            </div>
            <div className="space-y-2">
              <Label>No. WhatsApp *</Label>
              <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="628123456789" />
            </div>
            <div className="space-y-2">
              <Label>Email (fallback)</Label>
              <Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="admin@example.com" />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <select className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))}>
                <option value="admin">Admin</option>
                <option value="owner">Owner</option>
                <option value="developer">Developer</option>
                <option value="finance">Finance</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Kategori Notifikasi</Label>
              <div className="space-y-2 mt-1">
                {Object.entries(CATEGORY_LABELS).map(([key, { label }]) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.categories.includes(key)} onChange={() => toggleCat(key)} className="rounded border-slate-300" />
                    <span className="text-sm text-slate-700">{label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={v => setForm(p => ({ ...p, is_active: v }))} />
              <Label>Aktif</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Batal</Button>
            <Button onClick={save} className="bg-[#2D6CDF] hover:bg-[#2D6CDF]/90">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============================================================
// TAB 3: CONTROLS
// ============================================================
function ControlsTab() {
  const [controls, setControls] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/notification-controls').then(res => setControls(res.data)).catch(() => toast.error('Gagal memuat')).finally(() => setLoading(false));
  }, []);

  const toggle = (key, val) => setControls(p => ({ ...p, [key]: val }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/notification-controls', controls);
      toast.success('Kontrol notifikasi disimpan');
    } catch { toast.error('Gagal menyimpan'); }
    finally { setSaving(false); }
  };

  if (loading || !controls) return <Spinner />;

  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader>
        <CardTitle className="text-base">Kontrol Notifikasi</CardTitle>
        <p className="text-sm text-slate-500 mt-1">Atur kategori event mana yang mengirim notifikasi ke admin</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {Object.entries(CATEGORY_LABELS).map(([key, { label, desc }]) => (
          <div key={key} className="flex items-center justify-between p-4 rounded-xl border border-slate-200 bg-white">
            <div>
              <p className="font-medium text-slate-900">{label}</p>
              <p className="text-sm text-slate-500">{desc}</p>
            </div>
            <Switch checked={controls[key] !== false} onCheckedChange={v => toggle(key, v)} />
          </div>
        ))}

        {controls.high_transaction !== false && (
          <div className="p-4 rounded-xl border border-blue-200 bg-blue-50">
            <Label className="text-blue-800">Threshold Transaksi Besar (Rp)</Label>
            <Input type="number" className="mt-2 max-w-xs" value={controls.high_transaction_threshold || 1000000} onChange={e => toggle('high_transaction_threshold', parseInt(e.target.value) || 0)} />
            <p className="text-xs text-blue-600 mt-1">Notifikasi dikirim jika transaksi melebihi jumlah ini</p>
          </div>
        )}

        <div className="flex items-center justify-between p-4 rounded-xl border border-amber-200 bg-amber-50">
          <div>
            <p className="font-medium text-amber-900 flex items-center gap-2"><Shield size={16} />Mode Kritis</p>
            <p className="text-sm text-amber-700">Kirim semua notifikasi tanpa memandang kontrol di atas</p>
          </div>
          <Switch checked={controls.critical_mode || false} onCheckedChange={v => toggle('critical_mode', v)} />
        </div>

        <div className="flex justify-end pt-2">
          <Button onClick={save} disabled={saving} className="bg-[#2D6CDF] hover:bg-[#2D6CDF]/90">
            <Save size={16} className="mr-2" />{saving ? 'Menyimpan...' : 'Simpan Kontrol'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================
// TAB 4: LOGS
// ============================================================
function LogsTab() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ event: '', status: '', category: '' });

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (filters.event) params.set('event', filters.event);
      if (filters.status) params.set('status', filters.status);
      if (filters.category) params.set('category', filters.category);
      const res = await api.get(`/admin/notification-logs?${params}`);
      setLogs(res.data.logs);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
    } catch { toast.error('Gagal memuat log'); }
    finally { setLoading(false); }
  }, [page, filters]);

  useEffect(() => { fetch(); }, [fetch]);

  const setFilter = (k, v) => { setFilters(p => ({ ...p, [k]: v })); setPage(1); };

  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader>
        <CardTitle className="text-base">Log Notifikasi</CardTitle>
        <p className="text-sm text-slate-500 mt-1">Riwayat semua notifikasi admin yang terkirim ({total} total)</p>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-3 mb-4">
          <select className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm" value={filters.status} onChange={e => setFilter('status', e.target.value)}>
            <option value="">Semua Status</option>
            <option value="sent">Terkirim</option>
            <option value="failed">Gagal</option>
          </select>
          <select className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm" value={filters.category} onChange={e => setFilter('category', e.target.value)}>
            <option value="">Semua Kategori</option>
            {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm" value={filters.event} onChange={e => setFilter('event', e.target.value)}>
            <option value="">Semua Event</option>
            <option value="tenant_registered">Tenant Registered</option>
            <option value="billing_approved">Billing Approved</option>
            <option value="billing_rejected">Billing Rejected</option>
            <option value="new_subscription">New Subscription</option>
            <option value="affiliate_withdrawal">Affiliate Withdrawal</option>
            <option value="high_transaction">High Transaction</option>
            <option value="gateway_test">Gateway Test</option>
          </select>
        </div>

        {loading ? <Spinner /> : logs.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <ScrollText size={40} className="mx-auto mb-3 opacity-50" />
            <p>Belum ada log notifikasi</p>
          </div>
        ) : (
          <div className="space-y-2">
            {logs.map(log => (
              <div key={log.id} className="flex items-start justify-between p-3 rounded-lg border border-slate-100 bg-white text-sm">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="text-[10px]">{log.event}</Badge>
                    <Badge variant={log.status === 'sent' ? 'default' : 'destructive'} className={`text-[10px] ${log.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : ''}`}>
                      {log.status === 'sent' ? '✓ Terkirim' : '✗ Gagal'}
                    </Badge>
                    <Badge variant="secondary" className="text-[10px]">{log.channel}</Badge>
                  </div>
                  <p className="text-slate-600 mt-1 truncate">{log.recipient_name} — {log.recipient_phone}</p>
                  {log.error && <p className="text-red-500 text-xs mt-0.5">Error: {log.error}</p>}
                </div>
                <span className="text-xs text-slate-400 shrink-0 ml-2">{new Date(log.created_at).toLocaleString('id-ID')}</span>
              </div>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
            <p className="text-sm text-slate-500">Halaman {page} dari {totalPages}</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================
// TAB 5: BROADCAST
// ============================================================
function BroadcastTab() {
  const [message, setMessage] = useState('');
  const [channel, setChannel] = useState('whatsapp');
  const [recipientType, setRecipientType] = useState('active');
  const [sending, setSending] = useState(false);
  const [broadcasts, setBroadcasts] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    api.get('/admin/broadcasts?limit=10').then(res => setBroadcasts(res.data.broadcasts)).catch(() => {}).finally(() => setLoadingHistory(false));
  }, []);

  const send = async () => {
    if (!message.trim()) return toast.error('Pesan tidak boleh kosong');
    if (!window.confirm(`Kirim broadcast ke ${recipientType === 'all' ? 'SEMUA' : 'AKTIF'} tenant via ${channel}?`)) return;
    setSending(true);
    try {
      const res = await api.post('/admin/broadcast', { message, channel, recipient_type: recipientType });
      toast.success(res.data.message);
      setMessage('');
      // Refresh history
      const hist = await api.get('/admin/broadcasts?limit=10');
      setBroadcasts(hist.data.broadcasts);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal mengirim broadcast');
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-6">
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Kirim Broadcast</CardTitle>
          <p className="text-sm text-slate-500 mt-1">Kirim pesan manual ke tenant via WhatsApp atau Email</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Pesan</Label>
            <Textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Tulis pesan broadcast..." rows={5} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Channel</Label>
              <select className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" value={channel} onChange={e => setChannel(e.target.value)}>
                <option value="whatsapp">WhatsApp</option>
                <option value="email">Email</option>
                <option value="both">WhatsApp + Email</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Penerima</Label>
              <select className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" value={recipientType} onChange={e => setRecipientType(e.target.value)}>
                <option value="active">Tenant Aktif & Trial</option>
                <option value="all">Semua Tenant</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <Button onClick={send} disabled={sending || !message.trim()} className="bg-[#2D6CDF] hover:bg-[#2D6CDF]/90">
              {sending ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Send size={16} className="mr-2" />}
              {sending ? 'Mengirim...' : 'Kirim Broadcast'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Riwayat Broadcast</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingHistory ? <Spinner /> : broadcasts.length === 0 ? (
            <p className="text-center py-8 text-slate-400">Belum ada broadcast</p>
          ) : (
            <div className="space-y-2">
              {broadcasts.map(b => (
                <div key={b.id} className="p-3 rounded-lg border border-slate-100 bg-white text-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px]">{b.channel}</Badge>
                      <Badge variant="secondary" className="text-[10px]">{b.recipient_type}</Badge>
                      <span className="text-slate-500">→ {b.recipient_count} tenant</span>
                    </div>
                    <span className="text-xs text-slate-400">{new Date(b.created_at).toLocaleString('id-ID')}</span>
                  </div>
                  <p className="text-slate-600 mt-2 line-clamp-2">{b.message}</p>
                  <div className="flex gap-3 mt-2 text-xs">
                    <span className="text-emerald-600">✓ {b.sent_count} terkirim</span>
                    {b.failed_count > 0 && <span className="text-red-500">✗ {b.failed_count} gagal</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Shared spinner
function Spinner() {
  return <div className="py-20 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>;
}
